import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const RegistryForm = ({ schema, data, onChange, readOnly = false }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleChange = (fieldName, value) => {
    const newData = { ...formData, [fieldName]: value };
    setFormData(newData);
    validateField(fieldName, value);
    if (onChange) {
      onChange(newData);
    }
  };

  const validateField = (fieldName, value) => {
    const field = schema.properties[fieldName];
    const newErrors = { ...errors };

    if (field.required && (!value || value.trim() === '')) {
      newErrors[fieldName] = 'Campo obbligatorio';
    } else if (field.maxLength && value && value.length > field.maxLength) {
      newErrors[fieldName] = `Lunghezza massima: ${field.maxLength} caratteri`;
    } else {
      delete newErrors[fieldName];
    }

    setErrors(newErrors);
  };

  const renderField = (fieldName, field) => {
    const value = formData[fieldName] || '';
    const error = errors[fieldName];

    // Handle relationship fields
    if (field.relationship) {
      return (
        <FormControl key={fieldName} fullWidth error={!!error}>
          <InputLabel>{field.name}</InputLabel>
          <Select
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            label={field.name}
          >
            {field.relationship.type === 'one-to-one' || field.relationship.type === 'many-to-one' ? (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            ) : null}
            {field.relationship.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    }

    // Handle regular fields
    switch (field.type) {
      case 'string':
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            error={!!error}
            helperText={error || field.description}
            inputProps={{ maxLength: field.maxLength }}
          />
        );
      case 'number':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="number"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            error={!!error}
            helperText={error || field.description}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            key={fieldName}
            control={
              <Checkbox
                checked={value}
                onChange={(e) => handleChange(fieldName, e.target.checked)}
              />
            }
            label={field.name}
          />
        );
      case 'date':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="date"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            error={!!error}
            helperText={error || field.description}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'datetime':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="datetime-local"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            error={!!error}
            helperText={error || field.description}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'time':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="time"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            error={!!error}
            helperText={error || field.description}
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {schema.title || 'Dati Anagrafica'}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {schema.description}
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(schema.properties).map(([fieldName, field]) => (
          <Grid item xs={12} sm={6} key={fieldName}>
            {renderField(fieldName, field)}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default RegistryForm; 